/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { Slack, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSWRConfig } from 'swr';
import { z } from 'zod';

import StatusPopoverInfo from './StatusPopoverInfo';

import { Subscription } from '@/commons/protocols';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { getDirtyValues } from '@/lib/utils';
import { ErrorWithInfo, useMutate } from '@/net/commons';

const formSchema = z.object({
    name: z.string().trim().min(1, { message: 'Name is too short' }).max(100, { message: 'Name is too long' }),
    types: z.array(z.enum(['status'])),
    secret: z.string().trim().min(10, { message: 'Secret is too short' }).max(100, { message: 'Secret is too long' }),
    callback: z
        .string()
        .trim()
        .url()
        .regex(/^https:\/\//, 'Invalid https url'),
});

const items = [
    {
        id: 'status',
        label: 'Status',
        InfoComp: StatusPopoverInfo,
    },
] as const;

type SubscriptionDialogProps = {
    subscription?: Subscription;
    onClose?: () => void;
};

export default function SubscriptionDialogContent({ subscription, onClose }: SubscriptionDialogProps) {
    const [isLoading, setIsLoading] = useState(false);
    const isSlack = subscription?.type === 'slack';
    const isEditing = !!subscription;

    const form = useForm<z.infer<typeof formSchema>>({
        defaultValues: {
            name: subscription?.name || '',
            types: subscription?.types || ['status'],
            secret: subscription ? '**********' : '',
            callback: subscription?.callback || '',
        },
        resolver: zodResolver(formSchema),
    });

    const { mutate } = useSWRConfig();
    const { trigger: createSub } = useMutate(`/bff/organisation/subscriptions`, {
        revalidate: true,
        method: 'POST',
    });
    const { trigger: editSub } = useMutate(`/bff/organisation/subscriptions/:id`, {
        revalidate: true,
        method: 'PATCH',
        onSuccess: () => {
            mutate('/bff/organisation/subscriptions');
        },
    });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            setIsLoading(true);
            if (isEditing) {
                if (isSlack) {
                    // For Slack, we only allow updating types, so we force that to be the only field
                    await editSub({ idParam: subscription.id, types: values.types });
                } else {
                    const editedValues = getDirtyValues(form.formState.dirtyFields, values);
                    await editSub({ idParam: subscription.id, ...editedValues });
                }
            } else {
                await createSub(values);
            }
            onClose?.();
        } catch (err) {
            setIsLoading(false);
            const message = (err as ErrorWithInfo)?.info?.error ?? 'There was a problem with your request';
            toast({
                title: message,
                variant: 'destructive',
            });
        }
    };

    return (
        <DialogContent>
            <DialogHeader>
                <div className="flex items-center gap-2">
                    {isSlack ? (
                        <>
                            <Slack size={18} className="text-blue-500" />
                            <DialogTitle>Slack Integration</DialogTitle>
                        </>
                    ) : (
                        <>
                            <MessageSquare size={18} className="text-purple-500" />
                            <DialogTitle>{isEditing ? 'Edit' : 'Create'} Webhook Subscription</DialogTitle>
                        </>
                    )}
                </div>
                <DialogDescription className="mt-2">
                    {isSlack ? (
                        <>
                            Your Slack integration is configured to send notifications to the&nbsp;
                            <span className="font-semibold">
                                {subscription?.slackChannelId ? subscription.name : 'unknown'}
                            </span>{' '}
                            channel.
                        </>
                    ) : (
                        'Configure your webhook to receive notifications when something happens.'
                    )}
                </DialogDescription>
            </DialogHeader>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-10">
                    <div className="grid gap-4 py-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex flex-row gap-2 items-center">
                                        {isSlack ? 'Channel Name' : 'Subscription Name'}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="Example: My webhook"
                                            {...field}
                                            readOnly={isSlack}
                                            disabled={isSlack}
                                            className={isSlack ? 'bg-gray-50 cursor-not-allowed' : ''}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                    <FormDescription>
                                        {isSlack
                                            ? 'The Slack channel where notifications will be sent.'
                                            : 'A descriptive name to help you identify this webhook subscription.'}
                                    </FormDescription>
                                </FormItem>
                            )}
                        />

                        {!isSlack && (
                            <>
                                <FormField
                                    control={form.control}
                                    name="callback"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="flex flex-row gap-2 items-center">
                                                Callback URL
                                            </FormLabel>
                                            <FormControl>
                                                <Input placeholder="https://yourwebsite.com/webhook" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                            <FormDescription>
                                                The URL where webhook notifications will be sent. Must be a publicly
                                                accessible HTTPS endpoint that can receive POST requests.
                                            </FormDescription>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="secret"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="flex flex-row gap-2 items-center">Secret</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                            <FormMessage />
                                            <FormDescription>
                                                This secret will be used to sign the payload so you can verify the
                                                webhook is from us.
                                            </FormDescription>
                                        </FormItem>
                                    )}
                                />
                            </>
                        )}

                        <FormField
                            control={form.control}
                            name="types"
                            render={() => (
                                <FormItem>
                                    <div className="mb-4">
                                        <FormLabel className="text-base">When should we notify?</FormLabel>
                                        <FormDescription>
                                            Select the types of notifications you want to receive.
                                        </FormDescription>
                                    </div>
                                    {items.map((item) => (
                                        <FormField
                                            key={item.id}
                                            control={form.control}
                                            name="types"
                                            render={({ field }) => {
                                                return (
                                                    <FormItem
                                                        key={item.id}
                                                        className="flex flex-row space-y-0 space-x-2 items-center"
                                                    >
                                                        <FormControl>
                                                            <Checkbox
                                                                disabled
                                                                checked={field.value?.includes(item.id)}
                                                                onCheckedChange={(checked) => {
                                                                    return checked
                                                                        ? field.onChange([...field.value, item.id])
                                                                        : field.onChange(
                                                                              field.value?.filter(
                                                                                  (value) => value !== item.id,
                                                                              ),
                                                                          );
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormLabel className="text-black">{item.label}</FormLabel>
                                                        {item.InfoComp && <item.InfoComp />}
                                                    </FormItem>
                                                );
                                            }}
                                        />
                                    ))}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <DialogFooter>
                        {/* For Slack, we don't allow for now editing the subscription */}
                        {isSlack ? (
                            <Button variant="outline" onClick={onClose}>
                                Close
                            </Button>
                        ) : (
                            <Button type="submit" disabled={isLoading}>
                                {isLoading
                                    ? `${isEditing ? 'Editing...' : 'Creating...'}`
                                    : `${isEditing ? 'Edit' : 'Create'} Subscription`}
                            </Button>
                        )}
                    </DialogFooter>
                </form>
            </Form>
        </DialogContent>
    );
}
